import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Icon } from '@ascend/shared/components';
import { Button } from '@openzeppelin/ui-components';
import { ClaimsTable } from '../components/ClaimsTable';
import { LiquidationQueueTable } from '../components/LiquidationQueueTable';
import { getModuleConfig } from '../data/moduleConfigs';
import {
  DataTable,
  FilterTabs,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import {
  adapter,
  ADMIN_AUCTIONS,
  ADMIN_LIQUIDATION_HISTORY,
  getFacilityByName,
  getFacilityNameByAccountId,
  getCollateralBreakdownByFacility,
  getFacilityScopedAccounts,
} from '../data/mockAdapter';
import type { CfModuleWhitelistEntry } from '@ascend/mocked-shared/mock-data';

// ─── Types ────────────────────────────────────────────────────────────────────

type FacilityStatus = 'active' | 'paused' | 'shutdown';
type AccountStatus = 'active' | 'warn' | 'default' | 'paused' | 'frozen' | 'liquidated';
type SortDir = 'asc' | 'desc' | null;

interface MarginAccount {
  id: string;
  wallet: string;
  status: AccountStatus;
  collateralValue: number;
  borrowed: number;
  availableCredit: number;
  healthFactor: number;
  lastPayment: string;
  nextPayment: string;
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'margin-accounts' | 'claims' | 'policy' | 'access-control' | 'ddf-reserve' | 'modules' | 'liquidation';

const TABS: { id: TabId; label: string; icon?: React.ReactNode; badge?: number }[] = [
  { id: 'overview', label: 'Overview', icon: <Icon name="streamline:heart-rate-pulse-graph" className="h-4 w-4" /> },
  { id: 'margin-accounts', label: 'Margin Accounts', icon: <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" /> },
  { id: 'claims', label: 'Claims', icon: <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4" /> },
  { id: 'policy', label: 'Policy', icon: <Icon name="streamline:shield-check" className="h-4 w-4" /> },
  { id: 'access-control', label: 'Access Control', icon: <Icon name="streamline:key" className="h-4 w-4" /> },
  { id: 'ddf-reserve', label: 'DDF & Reserve', icon: <Icon name="streamline:travel-wayfinder-lifebuoy-water-life-ring-wheely-lifebelt-kisbee" className="h-4 w-4" /> },
  { id: 'modules', label: 'Modules', icon: <Icon name="streamline:module-three" className="h-4 w-4" /> },
  { id: 'liquidation', label: 'Liquidation', icon: <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-4 w-4" /> },
];

// ─── Access Control Types & Mock Data ─────────────────────────────────────────

type FacilityRole = 'ADMIN' | 'OPERATOR' | 'COMPLIANCE_OFFICER';

interface RoleAssignment {
  id: string;
  role: FacilityRole;
  walletAddress: string;
  label?: string;
  grantedAt: string;
}

const ROLE_DESCRIPTIONS: Record<FacilityRole, string> = {
  ADMIN: 'Full configuration authority. Can grant and revoke any role.',
  OPERATOR: 'Run-time operations: pause facility, freeze accounts, clawback.',
  COMPLIANCE_OFFICER: 'Approve / reject claim attestations from KYC providers.',
};

const INITIAL_ROLES: RoleAssignment[] = [
  { id: 'r-1', role: 'ADMIN', walletAddress: '0xA1b2C3d4E5f6789012345678901234567890aBcD', label: 'Deploy signer', grantedAt: '2026-04-10T14:22:00Z' },
  { id: 'r-2', role: 'OPERATOR', walletAddress: '0xB2c3D4e5F6a7b8c9d0e1f2a3b4c5d6e7f8a9B0c1', label: 'Ops Lead', grantedAt: '2026-04-12T09:15:00Z' },
  { id: 'r-3', role: 'OPERATOR', walletAddress: '0xC3d4E5f6A7b8C9d0E1f2A3b4C5d6E7f8A9b0C1d2', grantedAt: '2026-04-18T16:48:00Z' },
  { id: 'r-4', role: 'COMPLIANCE_OFFICER', walletAddress: '0xD4e5F6a7B8c9D0e1F2a3B4c5D6e7F8a9B0c1D2e3', label: 'Compliance Officer', grantedAt: '2026-04-15T11:02:00Z' },
];

function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<AccountStatus, string> = {
  active: 'bg-green-100 text-green-700',
  warn: 'bg-yellow-100 text-yellow-700',
  default: 'bg-red-100 text-red-700',
  paused: 'bg-yellow-100 text-yellow-700',
  frozen: 'bg-yellow-100 text-yellow-700',
  liquidated: 'bg-gray-100 text-gray-700',
};

const FACILITY_STATUS_CLASS: Record<FacilityStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
  shutdown: { label: 'Shutdown', className: 'bg-red-100 text-red-700' },
};

function formatCurrency(n: number): string {
  if (n < 0) return `-$${Math.abs(n).toLocaleString()}`;
  return `$${n.toLocaleString()}`;
}

function parseCurrencyString(value: string): number {
  const normalized = value.replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function parseDurationToMinutes(value: string): number {
  const hourMatch = value.match(/(\d+)\s*h/i);
  const minuteMatch = value.match(/(\d+)\s*m/i);
  const hours = hourMatch ? Number(hourMatch[1]) : 0;
  const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
  return hours * 60 + minutes;
}

function formatCompactUsd(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function healthFactorColor(hf: number): string {
  if (hf < 1.0) return 'text-red-600 font-semibold';
  if (hf <= 1.5) return 'text-yellow-600 font-semibold';
  return '';
}

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function Modal({ open, onClose, title, className, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative z-50 w-full rounded-lg border border-border bg-background p-6 shadow-xl ${className ?? 'max-w-md'}`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SortIcon({ dir }: { dir: SortDir }) {
  if (dir === 'asc') return <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="inline h-3 w-3 ml-0.5" />;
  if (dir === 'desc') return <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="inline h-3 w-3 ml-0.5" />;
  return <Icon name="streamline:interface-arrows-data-transfer-vertical-arrow-square-data-internet-transfer-network-vertical" className="inline h-3 w-3 ml-0.5 opacity-40" />;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CreditFacilityDetailPage() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const navigate = useNavigate();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const facility = useMemo(() => getFacilityByName(decodedName), [decodedName]);
  const facilityStatus: FacilityStatus = (facility?.status ?? 'active') as FacilityStatus;

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const status = FACILITY_STATUS_CLASS[facilityStatus];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-1.5 rounded-md p-1 hover:bg-accent transition-colors"
          >
            <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-5 w-5 text-foreground" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-bold">{decodedName}</h1>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
                {status.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Collaterals: {facility?.collateralTokens.join(', ') ?? 'N/A'} &middot; Settlements: {facility?.settlementTokens.join(', ') ?? 'N/A'}
            </p>
          </div>
        </div>
        <Button
          onClick={() =>
            toast.success(
              `Facility ${facilityStatus === 'paused' ? 'unpaused' : 'paused'} (mock)`,
            )
          }
          className={`flex items-center gap-2 ${
            facilityStatus === 'paused'
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-yellow-500 hover:bg-yellow-600 text-white'
          }`}
        >
          <Icon name="streamline:entertainment-control-button-pause-1-button-television-buttons-movies-tv-pause-video-controls" className="h-4 w-4" />
          {facilityStatus === 'paused' ? 'Unpause Facility' : 'Pause Facility'}
        </Button>
      </div>

      {/* Tab bar */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-1 border-b border-border min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.badge != null && (
                <span className="ml-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab autoRefresh={autoRefresh} onToggleAutoRefresh={() => setAutoRefresh(!autoRefresh)} />}
      {activeTab === 'margin-accounts' && <MarginAccountsTab />}
      {activeTab === 'claims' && <ClaimsTab />}
      {activeTab === 'policy' && <PolicyTab />}
      {activeTab === 'access-control' && <AccessControlTab />}
      {activeTab === 'ddf-reserve' && <DdfReserveTab />}
      {activeTab === 'modules' && <ModulesTab />}
      {activeTab === 'liquidation' && <LiquidationTab />}
    </div>
  );
}

// ─── Tab 1: Overview ──────────────────────────────────────────────────────────

function OverviewTab({ autoRefresh, onToggleAutoRefresh }: { autoRefresh: boolean; onToggleAutoRefresh: () => void }) {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const facility = useMemo(() => getFacilityByName(decodedName), [decodedName]);
  const accounts = useMemo(() => getFacilityScopedAccounts(decodedName), [decodedName]);

  const utilPct = facility?.utilizationPct ?? 60;
  const UTILIZATION_DATA = [
    { day: 'Mon', value: Math.round(utilPct * 0.92) },
    { day: 'Tue', value: Math.round(utilPct * 1.03) },
    { day: 'Wed', value: Math.round(utilPct * 0.97) },
    { day: 'Thu', value: Math.round(utilPct * 1.08) },
    { day: 'Fri', value: Math.round(utilPct * 1.17) },
    { day: 'Sat', value: Math.round(utilPct) },
    { day: 'Sun', value: Math.round(utilPct * 1.05) },
  ];

  const totalCollateral = accounts.reduce((sum, account) => sum + account.collateralValue, 0);
  const totalBorrowed = accounts.reduce((sum, account) => sum + account.borrowed, 0);
  const utilizationPct = totalCollateral > 0 ? (totalBorrowed / totalCollateral) * 100 : 0;
  const activeCount = accounts.filter((account) => account.status === 'active').length;
  const warnCount = accounts.filter((account) => account.status === 'warn').length;
  const defaultCount = accounts.filter((account) => account.status === 'default').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">Charts are mock and refresh in-memory only</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => toast.info('Data refreshed')}>
            <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button
            size="sm"
            variant={autoRefresh ? 'default' : 'outline'}
            onClick={onToggleAutoRefresh}
          >
            Auto-refresh {autoRefresh ? 'On' : 'Off'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard title="Total Collateral" value={formatCompactUsd(totalCollateral)} icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Total Borrowed" value={formatCompactUsd(totalBorrowed)} icon={<Icon name="streamline:money-cash-bag-dollar-bag-payment-cash-money-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Utilization" value={`${utilizationPct.toFixed(1)}%`} icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard
          title="Active Accounts"
          value={String(activeCount)}
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
          badges={[
            { label: `${warnCount} WARN`, className: 'bg-yellow-100 text-yellow-700' },
            { label: `${defaultCount} DEFAULT`, className: 'bg-red-100 text-red-700' },
          ]}
        />
      </div>

      <div className="rounded-xl border border-border p-5 sm:p-6">
        <h3 className="mb-4 text-lg font-semibold">Facility Configuration</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Object.entries({
            'Collateral Tokens': facility?.collateralTokens.join(', ') ?? 'N/A',
            'Settlement Tokens': facility?.settlementTokens.join(', ') ?? 'N/A',
            'Interest Rate': facility ? `${facility.interestRatePct}%` : 'N/A',
            'Liquidation Threshold': facility ? `${facility.liquidationThresholdPct}%` : 'N/A',
            'Max LTV': facility ? `${facility.maxLtvPct}%` : 'N/A',
            'Pool TVL': facility ? formatCompactUsd(facility.tvlUsd) : 'N/A',
            'Utilization': facility ? `${facility.utilizationPct.toFixed(1)}%` : 'N/A',
            'Borrow Rate': facility ? `${facility.interestRatePct}%` : 'N/A',
            'Total Borrowed': facility ? formatCompactUsd(facility.borrowedUsd) : 'N/A',
          }).map(([key, val]) => (
            <div key={key} className="flex justify-between rounded-lg bg-muted/30 px-3 py-2">
              <span className="text-sm text-muted-foreground">{key}</span>
              <span className="text-sm font-medium">{val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Charts - fix #1: use explicit pixel heights, #2: alignment, #3: vertical collateral */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CollateralBreakdownChart facilityName={decodedName} />
        <UtilizationChart currentUtilization={utilizationPct} utilizationData={UTILIZATION_DATA} />
        <AccountsByStatusChart accounts={accounts} />
      </div>
    </div>
  );
}

const TOKEN_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-orange-400', 'bg-purple-500', 'bg-pink-500', 'bg-cyan-500'];

function CollateralBreakdownChart({ facilityName }: { facilityName: string }) {
  const [segments, setSegments] = useState<Array<{ label: string; value: number; color: string }>>([]);

  useEffect(() => {
    getCollateralBreakdownByFacility(facilityName).then((breakdown) => {
      setSegments(breakdown.map((b, i) => ({ label: b.token, value: b.valueUsd, color: TOKEN_COLORS[i % TOKEN_COLORS.length] })));
    });
  }, [facilityName]);

  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Collateral Breakdown</h3>
        <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="space-y-3">
        {segments.map((s) => (
          <div key={s.label} className="group relative space-y-1">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                <span className="text-muted-foreground">{s.label}</span>
              </div>
              <span className="font-medium">{formatCompactUsd(s.value)}</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden cursor-pointer">
              <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${total > 0 ? (s.value / total) * 100 : 0}%` }} />
            </div>
            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 rounded-md bg-foreground px-2.5 py-1.5 text-[11px] font-medium text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100 whitespace-nowrap z-10">
              {s.label}: {formatCompactUsd(s.value)} ({total > 0 ? ((s.value / total) * 100).toFixed(1) : '0.0'}%)
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-xs text-muted-foreground">Total: {formatCompactUsd(total)}</div>
    </div>
  );
}

/* Fix #1: bars use explicit pixel calc, #2: left/right alignment */
function UtilizationChart({ currentUtilization, utilizationData }: { currentUtilization: number; utilizationData: { day: string; value: number }[] }) {
  const maxVal = Math.max(...utilizationData.map((d) => d.value));
  const barH = 112;
  const avgUtilization = utilizationData.reduce((sum, day) => sum + day.value, 0) / utilizationData.length;

  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Utilization Over Time (7 Days)</h3>
        <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex items-end gap-1.5" style={{ height: barH }}>
        {utilizationData.map((d) => (
          <div key={d.day} className="group relative flex flex-1 flex-col items-center justify-end h-full">
            <div
              className="w-full rounded-t bg-blue-500 cursor-pointer transition-colors hover:bg-blue-400"
              style={{ height: `${Math.round((d.value / maxVal) * barH * 0.85)}px` }}
            />
            <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded-md bg-foreground px-2.5 py-1.5 text-[11px] font-medium text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100 whitespace-nowrap z-10">
              {d.day}: {d.value}%
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        {utilizationData.map((d) => (
          <div key={d.day + '-label'} className="flex-1 text-center text-[10px] text-muted-foreground">{d.day}</div>
        ))}
      </div>
      <div className="mt-3 flex justify-between text-xs text-muted-foreground">
        <span>Avg: {avgUtilization.toFixed(1)}%</span>
        <span>Current: {currentUtilization.toFixed(1)}%</span>
      </div>
    </div>
  );
}

function AccountsByStatusChart({ accounts }: { accounts: MarginAccount[] }) {
  const data = [
    { label: 'Active', value: accounts.filter((account) => account.status === 'active').length, color: 'bg-green-500' },
    { label: 'Warn', value: accounts.filter((account) => account.status === 'warn').length, color: 'bg-yellow-500' },
    { label: 'Default', value: accounts.filter((account) => account.status === 'default').length, color: 'bg-red-500' },
  ];
  const maxVal = Math.max(1, ...data.map((entry) => entry.value));
  const barH = 112;

  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Accounts by Status</h3>
        <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex items-end gap-3" style={{ height: barH }}>
        {data.map((d) => {
          const total = data.reduce((sum, item) => sum + item.value, 0);
          return (
            <div key={d.label} className="group relative flex flex-1 flex-col items-center justify-end h-full">
              <span className="text-xs font-medium mb-1">{d.value}</span>
              <div
                className={`w-full rounded-t ${d.color} cursor-pointer transition-opacity hover:opacity-80`}
                style={{ height: `${Math.round((d.value / maxVal) * barH * 0.85)}px` }}
              />
              <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded-md bg-foreground px-2.5 py-1.5 text-[11px] font-medium text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100 whitespace-nowrap z-10">
                {d.label}: {d.value} accounts ({((d.value / total) * 100).toFixed(0)}%)
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 mt-1">
        {data.map((d) => (
          <div key={d.label + '-label'} className="flex-1 text-center text-[10px] text-muted-foreground">{d.label}</div>
        ))}
      </div>
      <div className="mt-3 flex justify-between text-xs text-muted-foreground">
        <span>Total Accounts: {accounts.length}</span>
        <span>At Risk: {data[1]!.value + data[2]!.value}</span>
      </div>
    </div>
  );
}

// ─── Tab 2: Margin Accounts (fix #4: sortable headers) ───────────────────────

type MaSortKey = 'id' | 'wallet' | 'status' | 'collateralValue' | 'borrowed' | 'availableCredit' | 'healthFactor';

const ACCOUNT_FILTERS: { value: AccountStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'warn', label: 'Warn' },
  { value: 'default', label: 'Default' },
  { value: 'paused', label: 'Paused' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'liquidated', label: 'Liquidated' },
];

function MarginAccountsTab() {
  const navigate = useNavigate();
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AccountStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState<MaSortKey>('healthFactor');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const facilityAccounts = useMemo(() => getFacilityScopedAccounts(decodedName), [decodedName]);

  function toggleSort(key: MaSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const filtered = useMemo(() => {
    let result = [...facilityAccounts];
    if (filter !== 'all') result = result.filter((a) => a.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) => a.wallet.toLowerCase().includes(q) || a.id.toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => {
      const cmp =
        typeof a[sortKey] === 'number'
          ? (a[sortKey] as number) - (b[sortKey] as number)
          : String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [facilityAccounts, filter, search, sortDir, sortKey]);

  const paginated = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    return filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  }, [filtered, page, pageSize]);

  const filterCounts = useMemo(() => {
    const counts: Partial<Record<AccountStatus | 'all', number>> = {
      all: facilityAccounts.length,
      active: 0,
      warn: 0,
      default: 0,
      paused: 0,
      frozen: 0,
      liquidated: 0,
    };
    for (const account of facilityAccounts) {
      counts[account.status] = (counts[account.status] ?? 0) + 1;
    }
    return counts;
  }, [facilityAccounts]);

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: 'Account ID',
        sortable: true,
        sortKey: 'id' as const,
        renderCell: (a: MarginAccount) => <span className="font-mono text-xs">{a.id}</span>,
      },
      {
        id: 'wallet',
        label: 'Wallet',
        sortable: true,
        sortKey: 'wallet' as const,
        renderCell: (a: MarginAccount) => <span className="font-mono text-xs">{a.wallet}</span>,
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        sortKey: 'status' as const,
        renderCell: (a: MarginAccount) => (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_CLASS[a.status]}`}>
            {a.status}
          </span>
        ),
      },
      {
        id: 'collateralValue',
        label: 'Collateral Value',
        align: 'right' as const,
        sortable: true,
        sortKey: 'collateralValue' as const,
        renderCell: (a: MarginAccount) => formatCurrency(a.collateralValue),
      },
      {
        id: 'borrowed',
        label: 'Borrowed',
        align: 'right' as const,
        sortable: true,
        sortKey: 'borrowed' as const,
        renderCell: (a: MarginAccount) => formatCurrency(a.borrowed),
      },
      {
        id: 'availableCredit',
        label: 'Available Credit',
        align: 'right' as const,
        sortable: true,
        sortKey: 'availableCredit' as const,
        renderCell: (a: MarginAccount) => formatCurrency(a.availableCredit),
      },
      {
        id: 'healthFactor',
        label: 'Health Factor',
        align: 'right' as const,
        sortable: true,
        sortKey: 'healthFactor' as const,
        renderCell: (a: MarginAccount) => (
          <span className={healthFactorColor(a.healthFactor)}>{a.healthFactor.toFixed(2)}</span>
        ),
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'right' as const,
        renderCell: (a: MarginAccount) => (
          <details
            className="relative inline-block text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <summary
              className="cursor-pointer list-none rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent select-none inline-flex items-center gap-1"
            >
              Actions <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-3 w-3 opacity-60" />
            </summary>
            <div className="absolute right-0 z-10 mt-1 w-48 rounded-md border border-border bg-card shadow-md py-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toast.success(`Account ${a.id} ${a.status === 'frozen' ? 'unfrozen' : 'frozen'} (mock)`);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
              >
                {a.status === 'frozen' ? 'Unfreeze account' : 'Freeze account'}
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toast.success(`Clawback initiated on ${a.id} (mock)`);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
              >
                Clawback
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  toast.success(`Migration started for ${a.id} (mock)`);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
              >
                Migrate / recover
              </button>
            </div>
          </details>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <FilterTabs<AccountStatus | 'all'>
          filters={ACCOUNT_FILTERS}
          active={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
          counts={filterCounts}
        />
        <SearchBar
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by wallet or account ID"
          className="w-full sm:w-80"
        />
      </div>

      <DataTable<MarginAccount, MaSortKey>
        columns={columns}
        rows={paginated}
        rowKey={(row) => row.id}
        sort={{
          key: sortKey,
          direction: sortDir,
          onSort: toggleSort,
        }}
        onRowClick={(row) => navigate(`/margin-accounts/${row.id}`)}
        rowClassName={() => 'cursor-pointer'}
        emptyMessage="No margin accounts match your filters."
      />

      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filtered.length}
        label="margin accounts"
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}

// ─── Tab 3: Claims ────────────────────────────────────────────────────────────

function ClaimsTab() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">Credit Facility Claims</h3>
          <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            Facility Scoped
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Ascend is the issuer of every claim. Providers (Sumsub, Onfido) supply
          attestation data; the operator approves or rejects.
        </p>
        <ClaimsTable facilityName={decodedName} />
      </div>
    </div>
  );
}

// ─── Tab 4: Policy (Identity Registry + Claim Requirements + Compliance Module) ───

function useScrollSpy(sectionIds: readonly string[], refs: React.MutableRefObject<Record<string, HTMLDivElement | null>>) {
  const [active, setActive] = useState(sectionIds[0]);
  const isManualScroll = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isManualScroll.current) return;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.getAttribute('data-section') ?? sectionIds[0]);
            break;
          }
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 },
    );
    for (const id of sectionIds) {
      const el = refs.current[id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sectionIds, refs]);

  const scrollTo = useCallback(
    (id: string) => {
      setActive(id);
      isManualScroll.current = true;
      refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => { isManualScroll.current = false; }, 800);
    },
    [refs],
  );

  return { active, scrollTo };
}

function PolicyTab() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = facilityName ? decodeURIComponent(facilityName) : '';
  // Identity Registry policy bound at deploy-time per spec §11.2 — display-only.
  // - Claim topics (KYC, Accredited, Partner Membership, etc.) live in the IR's CTR.
  // - Trusted issuers per topic live in the IR's TIR (always Ascend in this setup,
  //   per spec §10: providers like Sumsub supply data, Ascend signs the claim).
  // - Jurisdiction is a first-class field on the IR per spec §5, NOT a claim.
  const [boundRegistry] = useState({
    name: 'Ascend Default Registry',
    sharedWith: 4, // number of other facilities sharing this IR
    trustedIssuer: '0xA5cE1d000000000000000000000000000000ABcD',
    jurisdictionFilter: 'US',
  });
  const [accreditations] = useState({ kyc: true, accredited: true, qualified: false, institutional: false });
  // Ascend-specific custom claims required by this facility (deploy-time bound).
  const [customClaims] = useState({ partnerMembership: true, corporateEmail: true, qualifiedPremium: false });
  // Compliance Module attached to this CF — per ERC-3643 / SEP-0057 there is one
  // IModularCompliance per token, with bundled rule modules (tx-frequency, borrow
  // bounds, jurisdiction caps, etc.) configured inside it. Bound at deploy via
  // the wizard; changing it means redeploying the facility (same treatment as
  // the other module slots). Resolved from the facility→modules binding seeded
  // in CF_FACILITY_MODULES.
  const [attachedComplianceModule, setAttachedComplianceModule] = useState<CfModuleWhitelistEntry | null>(null);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const facilities = await adapter.facilities.list();
      const facility = facilities.find((f) => f.name === decodedName);
      if (!facility) return;
      const mods = await adapter.modules.listByFacility(facility.id);
      const compliance = mods.find((m) => m.moduleType === 'Compliance');
      if (!cancelled) setAttachedComplianceModule(compliance ?? null);
    }
    load();
    return () => { cancelled = true; };
  }, [decodedName]);
  const complianceModuleConfig = attachedComplianceModule
    ? getModuleConfig(attachedComplianceModule.id)
    : undefined;

  /* Claim requirements: every claim topic the IR's CTR enforces for this facility.
     Per spec §2/§4: claim topics are in CTR, trusted issuers in TIR (always Ascend
     per spec §10). Jurisdiction is NOT a claim topic — it's a first-class IR field
     surfaced separately in the Identity Registry header above. */
  const claimRequirements = useMemo(() => {
    const items: { label: string; required: boolean; ascendSpecific?: boolean }[] = [];
    if (accreditations.kyc) items.push({ label: 'KYC verified', required: true });
    if (accreditations.accredited) items.push({ label: 'Accredited Investor', required: true });
    if (accreditations.qualified) items.push({ label: 'Qualified Purchaser', required: true });
    if (accreditations.institutional) items.push({ label: 'Institutional Investor', required: false });
    if (customClaims.partnerMembership) items.push({ label: 'Partner Membership', required: true, ascendSpecific: true });
    if (customClaims.corporateEmail) items.push({ label: 'Corporate Email Verified', required: true, ascendSpecific: true });
    if (customClaims.qualifiedPremium) items.push({ label: 'Qualified Premium Investor', required: false, ascendSpecific: true });
    if (items.length === 0) items.push({ label: 'No claim topics configured', required: false });
    return items;
  }, [accreditations, customClaims]);

  return (
    <div className="space-y-6">
      {/* Identity Registry header — bound IR, sharing model, jurisdiction filter
          (IR-level field per spec §5), and trusted issuer (TIR contents per spec §4). */}
      <div className="rounded-xl border border-border bg-muted/20 p-5 sm:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Identity Registry</h3>
            <p className="text-xs text-muted-foreground">
              The IR encodes who may use this facility. Claim topics + trusted issuers
              are bound at deploy. Read-only · to change, redeploy.
            </p>
          </div>
          <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
            {boundRegistry.sharedWith === 0
              ? 'Dedicated to this facility'
              : `Shared with ${boundRegistry.sharedWith} other facilities`}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <span className="block text-xs uppercase tracking-wider text-muted-foreground">Bound registry</span>
            <span className="text-sm font-medium">{boundRegistry.name}</span>
          </div>
          <div>
            <span className="block text-xs uppercase tracking-wider text-muted-foreground">Jurisdiction filter</span>
            <span className="text-sm font-medium">{boundRegistry.jurisdictionFilter}</span>
          </div>
          <div>
            <span className="block text-xs uppercase tracking-wider text-muted-foreground">Trusted issuer</span>
            <span className="text-sm font-medium font-mono">
              Ascend · {boundRegistry.trustedIssuer.slice(0, 6)}…{boundRegistry.trustedIssuer.slice(-4)}
            </span>
          </div>
        </div>
      </div>

      {/* Claim Requirements (read-only summary derived from IR's CTR) */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Claim Requirements</h3>
          <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            From IR · ClaimTopicsRegistry
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Every claim topic a wallet must carry on its OnchainID to use this facility.
          Both regulated (KYC, accreditation) and Ascend-specific custom claims are
          enforced through the same registry.
        </p>
        <div className="space-y-3">
          {claimRequirements.map((c) => (
            <div key={c.label} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{c.label}</span>
                {c.ascendSpecific && (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-700">
                    Ascend-specific
                  </span>
                )}
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.required ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {c.required ? 'Required' : 'Optional'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Module — single IModularCompliance attached to this CF */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Compliance Module</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Evaluated on every borrow, deposit, withdraw, and transfer. Bound at
              deploy. Read-only · to change, redeploy.
            </p>
          </div>
          <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
            Runtime gate
          </span>
        </div>
        {attachedComplianceModule ? (
          <div className="rounded-lg bg-muted/30 px-4 py-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">{attachedComplianceModule.name}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  {attachedComplianceModule.chainAddress}
                </p>
              </div>
            </div>
            {complianceModuleConfig && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 pt-2 border-t border-border/60">
                {complianceModuleConfig.fields.map((f) => (
                  <div key={f.label} className="text-xs">
                    <span className="text-muted-foreground">{f.label}: </span>
                    <span className="font-medium">{f.value}</span>
                    {f.hint && <span className="block text-[11px] text-muted-foreground italic mt-0.5">{f.hint}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">Loading compliance module…</p>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Access Control (onchain role membership) ───────────────────────────

function AccessControlTab() {
  const [roles, setRoles] = useState<RoleAssignment[]>(INITIAL_ROLES);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingTx, setPendingTx] = useState<{ kind: 'grant' | 'revoke'; payload: string } | null>(null);

  // Track timer so we can cancel on unmount and avoid setState-on-unmounted-component
  // warnings during the simulated MetaMask round-trip.
  const txTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (txTimer.current !== null) {
        clearTimeout(txTimer.current);
        txTimer.current = null;
      }
    };
  }, []);

  const grouped = useMemo(() => {
    const buckets: Record<FacilityRole, RoleAssignment[]> = { ADMIN: [], OPERATOR: [], COMPLIANCE_OFFICER: [] };
    for (const r of roles) buckets[r.role].push(r);
    return buckets;
  }, [roles]);

  function handleSignAndGrant(role: FacilityRole, wallet: string, label?: string) {
    setPendingTx({ kind: 'grant', payload: `${role} → ${wallet}` });
    // Simulate MetaMask round-trip
    txTimer.current = setTimeout(() => {
      txTimer.current = null;
      setRoles((rs) => [
        ...rs,
        { id: `r-${Date.now()}`, role, walletAddress: wallet, label, grantedAt: new Date().toISOString() },
      ]);
      setPendingTx(null);
      setShowAddModal(false);
      toast.success(`Role ${role} granted to ${truncateAddress(wallet)}`);
    }, 1200);
  }

  function handleSignAndRevoke(assignment: RoleAssignment) {
    setPendingTx({ kind: 'revoke', payload: `${assignment.role} ← ${assignment.walletAddress}` });
    txTimer.current = setTimeout(() => {
      txTimer.current = null;
      setRoles((rs) => rs.filter((r) => r.id !== assignment.id));
      setPendingTx(null);
      toast.success(`Role ${assignment.role} revoked from ${truncateAddress(assignment.walletAddress)}`);
    }, 1200);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Onchain access control</h3>
          <p className="text-sm text-muted-foreground">
            Manage role membership on this credit facility. Grant and revoke
            actions are signed by the connected admin wallet.
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} disabled={!!pendingTx}>
          + Grant role
        </Button>
      </div>

      {pendingTx && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">Awaiting MetaMask signature… </span>
          {pendingTx.kind === 'grant' ? 'Granting' : 'Revoking'} {pendingTx.payload}
        </div>
      )}

      {(['ADMIN', 'OPERATOR', 'COMPLIANCE_OFFICER'] as const).map((role) => (
        <div key={role} className="rounded-xl border border-border p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold">{role}</h4>
              <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
            </div>
            <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {grouped[role].length} member{grouped[role].length === 1 ? '' : 's'}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Address</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Label</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Granted</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {grouped[role].length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No members</td></tr>
              ) : grouped[role].map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="px-3 py-2.5 font-mono text-xs">{truncateAddress(r.walletAddress)}</td>
                  <td className="px-3 py-2.5">{r.label ?? '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{new Date(r.grantedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => handleSignAndRevoke(r)}
                      disabled={!!pendingTx}
                      className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <GrantRoleModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSignAndGrant}
        pending={!!pendingTx}
      />
    </div>
  );
}

function GrantRoleModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (role: FacilityRole, wallet: string, label?: string) => void;
  pending: boolean;
}) {
  const [role, setRole] = useState<FacilityRole>('OPERATOR');
  const [wallet, setWallet] = useState('');
  const [label, setLabel] = useState('');

  const valid = /^0x[a-fA-F0-9]{40}$/.test(wallet);

  return (
    <Modal open={open} onClose={onClose} title="Grant role">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as FacilityRole)} className={INPUT_CLASS}>
            <option value="ADMIN">ADMIN</option>
            <option value="OPERATOR">OPERATOR</option>
            <option value="COMPLIANCE_OFFICER">COMPLIANCE_OFFICER</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Wallet address</label>
          <input
            type="text"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            placeholder="0x…"
            className={INPUT_CLASS}
          />
          {wallet && !valid && (
            <p className="mt-1 text-xs text-red-600">Must be a 0x-prefixed 40-char hex address.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Ops Lead"
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button onClick={() => onSubmit(role, wallet, label || undefined)} disabled={!valid || pending}>
            {pending ? 'Signing…' : 'Sign with MetaMask'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Tab 5: DDF & Reserve (fix #9: all sections + anchor nav) ─────────────────

const DDF_SECTIONS = [
  { id: 'reserve-overview', label: 'Reserve Overview', icon: <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-4 w-4" /> },
  { id: 'active-auctions', label: 'Active Auctions', icon: <Icon name="streamline:heart-rate-pulse-graph" className="h-4 w-4" /> },
  { id: 'resolution-records', label: 'Resolution Records', icon: <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4" /> },
  { id: 'reconciliation', label: 'Reconciliation', icon: <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-4 w-4" /> },
] as const;

function DdfReserveTab() {
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const ddfSectionIds = DDF_SECTIONS.map((s) => s.id);
  const { active: activeAnchor, scrollTo } = useScrollSpy(ddfSectionIds, sectionRefs);

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      <div className="w-full md:w-56 flex-shrink-0">
        <nav className="flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-x-visible md:sticky md:top-20">
          {DDF_SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeAnchor === s.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 space-y-6">
        <div ref={(el) => { sectionRefs.current['reserve-overview'] = el; }} data-section="reserve-overview" className="scroll-mt-24"><ReserveOverview /></div>
        <div ref={(el) => { sectionRefs.current['active-auctions'] = el; }} data-section="active-auctions" className="scroll-mt-24"><ActiveAuctions /></div>
        <div ref={(el) => { sectionRefs.current['resolution-records'] = el; }} data-section="resolution-records" className="scroll-mt-24"><ResolutionRecords /></div>
        <div ref={(el) => { sectionRefs.current['reconciliation'] = el; }} data-section="reconciliation" className="scroll-mt-24"><Reconciliation /></div>
      </div>
    </div>
  );
}

function ReserveOverview() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const records = useMemo(
    () => ADMIN_LIQUIDATION_HISTORY.filter((record) => getFacilityNameByAccountId(record.account) === decodedName),
    [decodedName],
  );
  const totalReserve = records.reduce((sum, record) => sum + parseCurrencyString(record.reserveAdvance), 0);
  const committed = records
    .filter((record) => record.auctionStatus === 'in-progress')
    .reduce((sum, record) => sum + parseCurrencyString(record.reserveAdvance), 0);
  const available = Math.max(totalReserve - committed, 0);
  const utilizationPct = totalReserve > 0 ? (committed / totalReserve) * 100 : 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard title="Total Reserve" value={formatCompactUsd(totalReserve)} subtitle="Pool capacity" icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Committed" value={formatCompactUsd(committed)} subtitle={`${utilizationPct.toFixed(1)}% utilized`} icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard
          title="Available"
          value={formatCompactUsd(available)}
          icon={<Icon name="streamline:check" className="h-5 w-5 text-muted-foreground" />}
          badges={[{ label: utilizationPct < 80 ? 'Healthy' : 'Constrained', className: utilizationPct < 80 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700' }]}
        />
      </div>
      <div className="rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Reserve Utilization</span>
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{utilizationPct.toFixed(1)}%</span>
        </div>
        <div className="h-3 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.min(utilizationPct, 100)}%` }} />
        </div>
      </div>
    </div>
  );
}

function ActiveAuctions() {
  const navigate = useNavigate();
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const { query, setQuery, page, setPage, pageSize, setPageSize, resetPage } = useTableControls(5);
  const [sortKey, setSortKey] = useState<
    'auctionId' | 'account' | 'currentBid' | 'reservePrice' | 'bidCount' | 'timeRemaining' | 'status'
  >('auctionId');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const auctions = ADMIN_AUCTIONS
    .filter((auction) => auction.facilityName === decodedName && auction.status !== 'settled')
    .map((auction) => ({
      auctionId: auction.id,
      account: auction.account,
      currentBid: auction.currentBid,
      reservePrice: auction.reservePrice,
      bidCount: auction.bidCount,
      timeRemaining: auction.timeRemaining,
      status: auction.status,
    }));

  const filteredAuctions = useMemo(() => {
    if (!query.trim()) return auctions;
    const q = query.toLowerCase();
    return auctions.filter(
      (auction) =>
        auction.auctionId.toLowerCase().includes(q) ||
        auction.account.toLowerCase().includes(q) ||
        auction.status.toLowerCase().includes(q),
    );
  }, [auctions, query]);
  const sortedAuctions = useMemo(() => {
    return [...filteredAuctions].sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'auctionId':
          comparison = a.auctionId.localeCompare(b.auctionId);
          break;
        case 'account':
          comparison = a.account.localeCompare(b.account);
          break;
        case 'currentBid':
          comparison = parseCurrencyString(a.currentBid) - parseCurrencyString(b.currentBid);
          break;
        case 'reservePrice':
          comparison = parseCurrencyString(a.reservePrice) - parseCurrencyString(b.reservePrice);
          break;
        case 'bidCount':
          comparison = a.bidCount - b.bidCount;
          break;
        case 'timeRemaining':
          comparison = parseDurationToMinutes(a.timeRemaining) - parseDurationToMinutes(b.timeRemaining);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [filteredAuctions, sortDir, sortKey]);
  const { rows: paginatedAuctions } = usePaginatedRows(sortedAuctions, page, pageSize);

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    resetPage();
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Active Auctions</h3>
      <SearchBar
        value={query}
        onChange={(value) => {
          setQuery(value);
          resetPage();
        }}
        placeholder="Search auction id or account..."
        className="w-full sm:w-[360px]"
      />
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th onClick={() => toggleSort('auctionId')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Auction
                <SortIcon dir={sortKey === 'auctionId' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('account')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Account
                <SortIcon dir={sortKey === 'account' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('currentBid')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Current Bid
                <SortIcon dir={sortKey === 'currentBid' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('reservePrice')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Reserve
                <SortIcon dir={sortKey === 'reservePrice' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('bidCount')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Bids
                <SortIcon dir={sortKey === 'bidCount' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('timeRemaining')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Time
                <SortIcon dir={sortKey === 'timeRemaining' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('status')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Status
                <SortIcon dir={sortKey === 'status' ? sortDir : null} />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedAuctions.map((auction) => (
              <tr
                key={auction.auctionId}
                onClick={() => navigate(`/cf/${encodeURIComponent(decodedName)}/auction/${auction.auctionId}`)}
                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
              >
                <td className="px-3 py-2.5 font-mono text-xs text-primary">{auction.auctionId}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{auction.account}</td>
                <td className="px-3 py-2.5 text-right">{auction.currentBid}</td>
                <td className="px-3 py-2.5 text-right">{auction.reservePrice}</td>
                <td className="px-3 py-2.5 text-right">{auction.bidCount}</td>
                <td className="px-3 py-2.5">{auction.timeRemaining}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${auction.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {auction.status.replace('_', ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={sortedAuctions.length}
        label="active auctions"
      />
    </div>
  );
}

function ResolutionRecords() {
  const navigate = useNavigate();
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const { page, setPage, pageSize, setPageSize } = useTableControls(5);
  const [sortKey, setSortKey] = useState<
    'auctionId' | 'account' | 'auctionStatus' | 'reserveAdvance' | 'proceeds' | 'variance' | 'finalStatus'
  >('auctionId');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const records = ADMIN_LIQUIDATION_HISTORY
    .filter((record) => getFacilityNameByAccountId(record.account) === decodedName)
    .map((record, index) => ({
      auctionId: `DDF-${String(2001 + index).padStart(4, '0')}`,
      account: record.account,
      auctionStatus: record.auctionStatus,
      reserveAdvance: record.reserveAdvance,
      proceeds: record.proceeds,
      variance: record.variance,
      finalStatus: record.finalStatus,
    }));
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'auctionId':
          comparison = a.auctionId.localeCompare(b.auctionId);
          break;
        case 'account':
          comparison = a.account.localeCompare(b.account);
          break;
        case 'auctionStatus':
          comparison = a.auctionStatus.localeCompare(b.auctionStatus);
          break;
        case 'reserveAdvance':
          comparison = parseCurrencyString(a.reserveAdvance) - parseCurrencyString(b.reserveAdvance);
          break;
        case 'proceeds':
          comparison = parseCurrencyString(a.proceeds) - parseCurrencyString(b.proceeds);
          break;
        case 'variance':
          comparison = parseCurrencyString(a.variance) - parseCurrencyString(b.variance);
          break;
        case 'finalStatus':
          comparison = a.finalStatus.localeCompare(b.finalStatus);
          break;
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [records, sortDir, sortKey]);
  const { rows: paginatedRecords } = usePaginatedRows(sortedRecords, page, pageSize);

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Resolution Records</h3>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th onClick={() => toggleSort('auctionId')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Auction
                <SortIcon dir={sortKey === 'auctionId' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('account')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Account
                <SortIcon dir={sortKey === 'account' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('auctionStatus')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Auction Status
                <SortIcon dir={sortKey === 'auctionStatus' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('reserveAdvance')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Reserve Advance
                <SortIcon dir={sortKey === 'reserveAdvance' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('proceeds')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Proceeds
                <SortIcon dir={sortKey === 'proceeds' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('variance')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground text-right cursor-pointer hover:text-foreground transition-colors">
                Variance
                <SortIcon dir={sortKey === 'variance' ? sortDir : null} />
              </th>
              <th onClick={() => toggleSort('finalStatus')} className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                Final Status
                <SortIcon dir={sortKey === 'finalStatus' ? sortDir : null} />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedRecords.map((record) => (
              <tr
                key={record.auctionId}
                onClick={() => navigate(`/cf/${encodeURIComponent(decodedName)}/auction/${record.auctionId}`)}
                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
              >
                <td className="px-3 py-2.5 font-mono text-xs text-primary">{record.auctionId}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{record.account}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${record.auctionStatus === 'settled' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {record.auctionStatus}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">{record.reserveAdvance}</td>
                <td className="px-3 py-2.5 text-right">{record.proceeds}</td>
                <td className="px-3 py-2.5 text-right">{record.variance}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${record.finalStatus === 'Settled' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {record.finalStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={sortedRecords.length}
        label="resolution records"
      />
    </div>
  );
}

function Reconciliation() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const settledRecords = useMemo(
    () =>
      ADMIN_LIQUIDATION_HISTORY.filter(
        (record) => getFacilityNameByAccountId(record.account) === decodedName && record.auctionStatus === 'settled',
      ),
    [decodedName],
  );
  const expectedReplenishment = settledRecords.reduce(
    (sum, record) => sum + parseCurrencyString(record.reserveAdvance),
    0,
  );
  const actualProceeds = settledRecords.reduce((sum, record) => sum + parseCurrencyString(record.proceeds), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Reserve Reconciliation</h3>
        <Button variant="outline" size="sm">Export Report</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard title="Expected Replenishment" value={formatCurrency(expectedReplenishment)} icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Actual Proceeds" value={formatCurrency(actualProceeds)} icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />} />
      </div>
    </div>
  );
}

// ─── Tab: Modules ─────────────────────────────────────────────────────────────

function ModulesTab() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const [modules, setModules] = useState<CfModuleWhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const facilities = await adapter.facilities.list();
      const facility = facilities.find((f) => f.name === decodedName);
      if (facility) {
        const mods = await adapter.modules.listByFacility(facility.id);
        setModules(mods);
      }
      setLoading(false);
    }
    load();
  }, [decodedName]);

  const TYPE_BADGE: Record<string, string> = {
    Fee: 'bg-green-100 text-green-700',
    Liquidity: 'bg-blue-100 text-blue-700',
    Risk: 'bg-red-100 text-red-700',
    Valuation: 'bg-purple-100 text-purple-700',
    Auction: 'bg-yellow-100 text-yellow-700',
    Oracle: 'bg-orange-100 text-orange-700',
  };

  if (loading) return <p className="text-muted-foreground">Loading modules...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Configured Modules</h3>
          <p className="text-sm text-muted-foreground">Modules assigned to this credit facility.</p>
        </div>
        <Link to="/module-management">
          <Button variant="outline" size="sm">Manage Modules</Button>
        </Link>
      </div>

      {modules.length === 0 ? (
        <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
          No modules configured for this facility.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left">
                <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Type</th>
                <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Chain Address</th>
                <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Description</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod) => (
                <tr key={mod.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2.5">
                    <Link to={`/module-management/${mod.id}`} className="font-medium text-primary hover:underline">{mod.name}</Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGE[mod.moduleType] ?? 'bg-gray-100 text-gray-700'}`}>
                      {mod.moduleType}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{mod.chainAddress}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{mod.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab 7: Liquidation (fix #11: modal, #12: simulated curve) ────────────────

function LiquidationTab() {
  const { facilityName } = useParams<{ facilityName: string }>();
  const decodedName = decodeURIComponent(facilityName ?? '');
  const [simulationType, setSimulationType] = useState<'borrow' | 'withdraw'>('borrow');
  const [simulationAmount, setSimulationAmount] = useState('');

  const amount = parseFloat(simulationAmount) || 0;
  const currentUtil = 70.0;
  const currentBorrow = 6.5;
  const currentLender = 4.5;
  const totalLiq = 10_000_000;
  const currentBorrowed = 7_000_000;

  let simUtil = currentUtil;
  let simBorrow = currentBorrow;
  let simLender = currentLender;
  if (amount > 0) {
    const newBorrowed = simulationType === 'borrow' ? currentBorrowed + amount : currentBorrowed;
    const newLiq = simulationType === 'withdraw' ? totalLiq - amount : totalLiq;
    simUtil = Math.min(99, (newBorrowed / newLiq) * 100);
    simBorrow = 2 + (simUtil / 100) * 10;
    simLender = simBorrow * (simUtil / 100);
  }

  return (
    <div className="space-y-6">
      <LiquidationQueueTable
        facilityName={decodedName}
        title="At-Risk & Default Queue"
        description="Facility-scoped liquidation queue. Uses the same component as global Liquidation Management."
      />

      {/* Simulator */}
      <div className="rounded-xl border border-border p-5 sm:p-6 space-y-5">
        <h3 className="text-lg font-semibold">Liquidity Rate Impact Simulator</h3>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground mr-2">Simulation Type:</span>
          <Button size="sm" variant={simulationType === 'borrow' ? 'default' : 'outline'} onClick={() => setSimulationType('borrow')}>
            Borrow More
          </Button>
          <Button size="sm" variant={simulationType === 'withdraw' ? 'default' : 'outline'} onClick={() => setSimulationType('withdraw')}>
            Withdraw Liquidity
          </Button>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Amount (USDC)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
            <input
              type="number"
              placeholder="100,000"
              value={simulationAmount}
              onChange={(e) => setSimulationAmount(e.target.value)}
              className={`${INPUT_CLASS} pl-7`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border p-5">
            <h4 className="mb-3 text-sm font-semibold">Current State</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Utilization</span><span className="font-medium">{currentUtil.toFixed(1)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Borrow Rate</span><span className="font-medium">{currentBorrow.toFixed(2)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Lender APY</span><span className="font-medium">{currentLender.toFixed(2)}%</span></div>
            </div>
          </div>
          <div className="rounded-xl border border-border p-5">
            <h4 className="mb-3 text-sm font-semibold">{amount > 0 ? 'Simulated State' : 'Enter amount to simulate'}</h4>
            {amount > 0 ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Utilization</span><span className="font-medium">{simUtil.toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Borrow Rate</span><span className="font-medium">{simBorrow.toFixed(2)}%</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Lender APY</span><span className="font-medium">{simLender.toFixed(2)}%</span></div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Enter an amount above to see projected impact</p>
            )}
          </div>
        </div>

        {/* Fix #12: Interest Rate Curve shows simulated marker */}
        <div className="rounded-xl border border-border p-5">
          <h4 className="mb-3 text-sm font-semibold">Interest Rate Curve</h4>
          <InterestRateCurve
            currentUtil={currentUtil}
            currentBorrow={currentBorrow}
            simUtil={amount > 0 ? simUtil : undefined}
            simBorrow={amount > 0 ? simBorrow : undefined}
          />
        </div>

        <p className="text-xs text-muted-foreground italic">
          This is a simulation only. No actual transaction will be executed.
        </p>
      </div>
    </div>
  );
}

const RATE_CURVE_DATA = Array.from({ length: 51 }, (_, i) => {
  const u = i * 2;
  const br = 2 + (u / 100) * 10;
  const lr = br * (u / 100);
  return { utilization: u, borrowRate: +br.toFixed(2), lenderAPY: +lr.toFixed(2) };
});

function RateCurveTooltip({ active, payload, label }: { active?: boolean; payload?: { color: string; name: string; value: number }[]; label?: number }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-lg text-xs">
      <p className="font-medium mb-1">Utilization: {label}%</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name}: {entry.value.toFixed(2)}%
        </p>
      ))}
    </div>
  );
}

function InterestRateCurve({ currentUtil, currentBorrow, simUtil, simBorrow }: {
  currentUtil: number;
  currentBorrow: number;
  simUtil?: number;
  simBorrow?: number;
}) {
  const hasSimulated = simUtil !== undefined && simBorrow !== undefined;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={RATE_CURVE_DATA} margin={{ top: 20, right: 12, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
        <XAxis
          dataKey="utilization"
          type="number"
          domain={[0, 100]}
          tickFormatter={(v: number) => `${v}%`}
          ticks={[0, 25, 50, 75, 100]}
          tick={{ fontSize: 11 }}
          stroke="currentColor"
          strokeOpacity={0.2}
        />
        <YAxis
          tickFormatter={(v: number) => `${v}%`}
          tick={{ fontSize: 11 }}
          domain={[0, 12]}
          ticks={[0, 4, 8, 12]}
          stroke="currentColor"
          strokeOpacity={0.2}
          width={36}
        />
        <RechartsTooltip content={<RateCurveTooltip />} />
        <RechartsLegend
          verticalAlign="bottom"
          height={28}
          iconType="line"
          wrapperStyle={{ fontSize: 11 }}
        />
        <Line
          type="monotone"
          dataKey="borrowRate"
          name="Borrow Rate"
          stroke="#2d7dd2"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0 }}
        />
        <Line
          type="monotone"
          dataKey="lenderAPY"
          name="Lender APY"
          stroke="#00cc66"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0 }}
        />
        <ReferenceLine
          x={currentUtil}
          stroke="#2d7dd2"
          strokeDasharray="4 4"
          strokeWidth={1}
          label={{ value: `Current ${currentBorrow}%`, position: 'top', fontSize: 10, fill: '#2d7dd2' }}
        />
        {hasSimulated && (
          <ReferenceLine
            x={simUtil}
            stroke="#ff3333"
            strokeDasharray="4 4"
            strokeWidth={1}
            label={{ value: `Sim ${simBorrow.toFixed(1)}%`, position: 'top', fontSize: 10, fill: '#ff3333' }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Shared Components ────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
  badges,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  badges?: { label: string; className: string }[];
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
      {badges && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {badges.map((badge) => (
            <span key={badge.label} className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
